/**
 * Edit mode's «Колоды» tab (handoff §15.4): the decks the text will be linked to once
 * saved, «Отвязать» on each, «Привязать колоду» with the picker. Linking and unlinking
 * go to the draft and touch no card. The picker's footnote says how words become
 * cards: «Слова» → «Отметить слова в тексте» (handoff §15.4, ADR-115 Amendment 3).
 *
 * ADR-115 Amendment 4: the picker also offers «Новая колода», a deck named after the
 * text (editable) and in the daily queue unless switched off, made only by the save.
 * The deck the cards go to is listed too, marked as such; it is changed in the deck
 * question on «Слова», not unlinked here.
 */
import { useState } from 'react'
import { Layers, Plus } from 'lucide-react'
import { useLocale } from '../../../i18n/context'
import { useDecks } from '../../../hooks/useDecks'
import { useTextEditStore } from '../../../stores/textEditStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../../utils/controlSurface'
import { draftLinks, madeDeck, type EditDraft } from '../../../utils/textEdit'
import DeckPickerSheet from '../DeckPickerSheet'
import { NewDeckFields } from './EditCards'
import { OutlinePill, SecondaryPill } from '../ui'

export default function DecksTab({ draft }: { draft: EditDraft }) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const store = useTextEditStore()
  const [picking, setPicking] = useState(false)
  // Card counts come from the deck list the app already has; a deck it does not hold shows none.
  const decks = useDecks(1, 100, null, undefined)
  const counts = new Map((decks.data?.items ?? []).map((d) => [d.id, d.card_count]))
  const links = draftLinks(draft)
  const making = madeDeck(draft) !== null

  return (
    <div
      data-testid="edit-decks-tab"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{ display: 'flex', flexDirection: 'column', gap: 12, fontFamily: 'var(--font-ui)' }}
    >
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{te.decksIntro}</p>

      {links.length === 0 ? (
        <span
          data-testid="edit-no-deck"
          style={{
            alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 12px',
            borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)',
          }}
        >
          {te.noDeckChip}
        </span>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          {links.map((link, i) => {
            const count = link.kind === 'existing' ? counts.get(link.id) : undefined
            const meta = link.receives ? te.deckReceives
              : link.kind === 'new' ? te.deckNewMeta
              : count !== undefined ? te.deckMeta(count) : null
            return (
              <li
                key={link.kind === 'existing' ? link.id : 'new'}
                data-testid={link.kind === 'new' ? 'edit-deck-new' : 'edit-deck-row'}
                style={{ padding: '12px 12px 12px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span
                    aria-hidden
                    style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'var(--bg-secondary)',
                      color: link.kind === 'new' ? 'var(--accent)' : 'var(--text-secondary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {link.kind === 'new' ? <Plus size={18} /> : <Layers size={18} />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {link.title}
                    </span>
                    {meta && (
                      <span data-testid={link.receives ? 'edit-deck-receives' : undefined} style={{ fontSize: 12, color: link.receives ? 'var(--accent)' : 'var(--text-secondary)' }}>
                        {meta}
                      </span>
                    )}
                  </span>
                  {!link.receives && (
                    <OutlinePill
                      onClick={() => (link.kind === 'new' ? store.unlinkNewDeck() : store.unlinkDeck(link.id))}
                      data-testid="edit-deck-unlink"
                      style={{ flexShrink: 0 }}
                    >
                      {te.unlink}
                    </OutlinePill>
                  )}
                </div>
                {/* The new deck's name and queue, here too when it came from «Привязать колоду». */}
                {link.kind === 'new' && draft.newDeck?.linked && (
                  <div style={{ marginTop: 12 }}><NewDeckFields draft={draft} /></div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <SecondaryPill full onClick={() => setPicking(true)} data-testid="edit-deck-link">{te.linkDeck}</SecondaryPill>
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>{te.noDeckFootnote}</p>

      <DeckPickerSheet
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(deck) => (draft.deckIds.includes(deck.id) ? store.unlinkDeck(deck.id) : store.linkDeck(deck))}
        newDeck={making ? undefined : {
          label: tr.textFlow.save.newDeck.title,
          onPick: () => { store.linkNewDeck(); setPicking(false) },
        }}
        linking={{
          linkedIds: draft.deckIds, title: te.pickerTitle, linked: te.pickerLinked, link: te.pickerLink,
          footnote: te.pickerFootnote,
        }}
      />
    </div>
  )
}
